import { useQuery, skipToken } from '@tanstack/react-query';
import type { DeliveryDetail } from '@citibox-panel/api-client';

export interface UseDeliveryDetail {
  detail: DeliveryDetail | undefined;
  isPending: boolean;
  // True while a fetch is in flight, including a background refetch — unlike isPending, which is
  // only the first load. Lets a caller show the loading skeleton over a post-write refetch.
  isFetching: boolean;
}

// The read-only «Entrega» detail, keyed by delivery UUID. The query is skipped
// (skipToken) until an id is known. `throwOnError` lets a fetch failure surface through
// the sub-tab's FeatureErrorBoundary — the whole sub-tab IS this detail, so there is no
// sibling card to protect (unlike the deliveries list, which keeps its error inline).
// app-service validates the Firebase token and re-checks the @citibox.com domain SERVER-side
// (AS-015 was closed as obsolete on 2026-08-18), so this read sits behind a real boundary — which
// matters more since it started carrying third-party PII. The panel's RequireAuth remains client
// UX only, and never the authorization boundary.
export function useDeliveryDetail(
  id: string | undefined,
  fetchDeliveryDetail: (id: string) => Promise<DeliveryDetail>,
): UseDeliveryDetail {
  const { data, isPending, isFetching } = useQuery({
    queryKey: ['delivery-detail', id],
    queryFn: id === undefined ? skipToken : () => fetchDeliveryDetail(id),
    throwOnError: true,
  });

  return { detail: data, isPending, isFetching };
}
